import { sessionAttribution, safePageUrl } from "./visitor-attribution";
import { mountEngagement } from "./visitor-engagement";
// =====================================================================
// Solufy Anonymous Website Visitor Tracking System
// =====================================================================

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes of inactivity
const FLUSH_INTERVAL_MS = 1500; // Batch send every 1.5s
const API_BATCH_ENDPOINT = "/api/method/solufy_website.api.visitor.record_events";

// ---------------------------------------------------------------------
// Cookie Helpers
// ---------------------------------------------------------------------
function getCookie(name) {
	const matches = document.cookie.match(
		new RegExp("(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)"),
	);
	return matches ? decodeURIComponent(matches[1]) : null;
}

function setCookie(name, value, days = 365) {
	const expires = new Date(Date.now() + days * 864e5).toUTCString();
	document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

// ---------------------------------------------------------------------
// Visitor & Session Identification (Purely Anonymous)
// ---------------------------------------------------------------------
function generateUUID() {
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}
	return "sol_" + Math.random().toString(36).substring(2, 11) + "_" + Date.now().toString(36);
}

export function getOrSetVisitorId() {
	let vid = getCookie("solufy_vid");
	if (!vid || vid.length < 8) {
		try {
			vid = localStorage.getItem("solufy_vid");
		} catch (e) {}
	}
	if (!vid || vid.length < 8) {
		vid = generateUUID();
	}
	// Always ensure persistent cookie and local storage
	setCookie("solufy_vid", vid, 365);
	try {
		localStorage.setItem("solufy_vid", vid);
	} catch (e) {}
	return vid;
}

export function getOrSetSessionId() {
	const vid = getOrSetVisitorId();
	const now = Date.now();
	let sid = getCookie("solufy_sid");
	let lastActive = 0;

	try {
		lastActive = parseInt(localStorage.getItem("solufy_last_active") || "0", 10);
		if (!sid) {
			sid = sessionStorage.getItem("solufy_sid");
		}
	} catch (e) {}

	const isExpired = !sid || !lastActive || now - lastActive > SESSION_TIMEOUT_MS;

	if (isExpired) {
		sid = "sess_" + vid.substring(0, 8) + "_" + now.toString(36) + "_" + Math.random().toString(36).substring(2, 6);
		try {
			sessionStorage.setItem("solufy_sid", sid);
			localStorage.setItem("solufy_sid", sid);
		} catch (e) {}
		setCookie("solufy_sid", sid, 1); // 1 day cookie or session
	}

	try {
		localStorage.setItem("solufy_last_active", now.toString());
	} catch (e) {}

	return { sessionId: sid, isNewSession: isExpired };
}

function updateLastActive() {
	try {
		const previous = Number(localStorage.getItem("solufy_last_active") || 0);
		if (Date.now() - previous < SESSION_TIMEOUT_MS) localStorage.setItem("solufy_last_active", Date.now().toString());
	} catch (e) {}
}

function getConsentStatus() {
	const consent = getCookie("solufy_consent") || (() => { try { return localStorage.getItem("solufy_consent"); } catch { return null; } })() || "Unknown";
	return consent;
}

// ---------------------------------------------------------------------
// Client & Page Context Extractor
// ---------------------------------------------------------------------
let clientGeoCache = null;

function resolveGeoFromTimezone(tz) {
	if (!tz) return null;
	const lower = tz.toLowerCase();
	if (lower.includes("calcutta") || lower.includes("kolkata")) {
		return { country: "India", city: "", region_state: "", latitude: 0, longitude: 0 };
	}
	if (lower.includes("new_york") || lower.includes("chicago") || lower.includes("los_angeles") || lower.includes("denver") || lower.includes("phoenix") || lower.startsWith("america/")) {
		return { country: "United States", city: "", region_state: "", latitude: 0, longitude: 0 };
	}
	if (lower.includes("london")) return { country: "United Kingdom", city: "", region_state: "", latitude: 0, longitude: 0 };
	if (lower.includes("toronto") || lower.includes("vancouver") || lower.includes("montreal")) return { country: "Canada", city: "", region_state: "", latitude: 0, longitude: 0 };
	if (lower.includes("berlin")) return { country: "Germany", city: "", region_state: "", latitude: 0, longitude: 0 };
	if (lower.includes("paris")) return { country: "France", city: "", region_state: "", latitude: 0, longitude: 0 };
	if (lower.includes("dubai")) return { country: "United Arab Emirates", city: "", region_state: "", latitude: 0, longitude: 0 };
	if (lower.includes("singapore")) return { country: "Singapore", city: "", region_state: "", latitude: 0, longitude: 0 };
	if (lower.includes("sydney") || lower.includes("melbourne")) return { country: "Australia", city: "", region_state: "", latitude: 0, longitude: 0 };
	if (lower.includes("tokyo")) return { country: "Japan", city: "", region_state: "", latitude: 0, longitude: 0 };
	return null;
}

function getClientGeo() {
	if (clientGeoCache && clientGeoCache.country) return clientGeoCache;

	// 1. Try solufy_geo cookie (discards stale fake hardcoded Ahmedabad coordinates)
	const geoCookie = getCookie("solufy_geo");
	if (geoCookie) {
		try {
			const parsed = typeof geoCookie === "string" ? JSON.parse(geoCookie) : geoCookie;
			if (parsed && (parsed.country || parsed.city)) {
				const isFake = (parsed.city === "Ahmedabad" && parsed.latitude >= 23.022 && parsed.latitude <= 23.023);
				if (!isFake) {
					clientGeoCache = parsed;
					return clientGeoCache;
				}
			}
		} catch (e) {}
	}

	// 2. Try solufy_country cookie
	const countryCookie = getCookie("solufy_country");
	if (countryCookie) {
		clientGeoCache = {
			country: countryCookie,
			city: getCookie("solufy_city") || "",
			region_state: getCookie("solufy_region") || "",
			latitude: 0,
			longitude: 0,
		};
		return clientGeoCache;
	}

	// 3. Fallback to client browser timezone resolution (Country only, NO fake coordinates)
	const tz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || "";
	if (tz) {
		const tzGeo = resolveGeoFromTimezone(tz);
		if (tzGeo && tzGeo.country) {
			clientGeoCache = tzGeo;
			return clientGeoCache;
		}
	}

	return clientGeoCache || {};
}

function getClientContext() {
	const w = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth || 0;
	let deviceType = "Desktop";
	if (w < 768) {
		deviceType = "Mobile";
	} else if (w < 1024) {
		deviceType = "Tablet";
	}

	const urlParams = new URLSearchParams(window.location.search);
	const geo = getClientGeo();

	return {
		screen_resolution: `${window.screen.width || 0}x${window.screen.height || 0}`,
		viewport_size: `${w}x${window.innerHeight || 0}`,
		device_type: deviceType,
		language: navigator.language || navigator.userLanguage || "",
		timezone: Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || "",
		utm_source: urlParams.get("utm_source") || "",
		utm_medium: urlParams.get("utm_medium") || "",
		utm_campaign: urlParams.get("utm_campaign") || "",
		utm_term: urlParams.get("utm_term") || "",
		utm_content: urlParams.get("utm_content") || "",
		country: geo.country || "",
		region_state: geo.region_state || "",
		city: geo.city || "",
		latitude: geo.latitude || 0,
		longitude: geo.longitude || 0,
	};
}

function detectPageDetails() {
	const path = window.location.pathname.toLowerCase();
	let pageType = "other";
	let serviceName = "";
	let serviceSlug = "";
	let industryName = "";
	let industrySlug = "";
	let articleName = "";
	let jobName = "";

	if (path === "/" || path === "") {
		pageType = "home";
	} else if (path.startsWith("/services")) {
		pageType = "service";
		const parts = path.split("/").filter(Boolean);
		if (parts.length > 1) {
			serviceSlug = parts[1];
			serviceName = document.querySelector("h1")?.textContent?.trim() || formatSlugToTitle(serviceSlug);
		} else {
			serviceSlug = "services-overview";
			serviceName = "Services Overview";
		}
	} else if (path.startsWith("/industries")) {
		pageType = "industry";
		const parts = path.split("/").filter(Boolean);
		if (parts.length > 1) {
			industrySlug = parts[1];
			industryName = document.querySelector("h1")?.textContent?.trim() || formatSlugToTitle(industrySlug);
		} else {
			industrySlug = "industries-overview";
			industryName = "Industries Overview";
		}
	} else if (path.startsWith("/blog") || path.startsWith("/articles")) {
		pageType = "article";
		articleName = document.title || "";
	} else if (path.startsWith("/careers") || path.startsWith("/jobs")) {
		pageType = "careers";
		jobName = document.querySelector("h1")?.textContent?.trim() || "";
	} else if (path.includes("contact") || path.includes("quote")) {
		pageType = "contact";
	} else if (path.includes("about")) {
		pageType = "about";
	}

	return {
		pageType,
		service: serviceSlug,
		serviceName,
		industry: industrySlug,
		industryName,
		article: articleName,
		articleName,
		job: jobName,
		jobName,
	};
}

function formatSlugToTitle(slug) {
	if (!slug) return "";
	return slug
		.replace(/-/g, " ")
		.replace(/erpnext/gi, "ERPNext")
		.replace(/erp/gi, "ERP")
		.replace(/\b\w/g, l => l.toUpperCase());
}

// ---------------------------------------------------------------------
// Event Queue & Batch Transport
// ---------------------------------------------------------------------
class VisitorTracker {
	constructor() {
		this.queue = [];
		this.failedBatches = 0;
		this.inFlight = false;
		this.flushTimer = null;
		this.pageStartTime = performance.now();
		this.maxScrollDepth = 0;
		this.activeFormStarts = new Set();
		this.initialized = false;
		this.currentPath = window.location.pathname;
	}

	init() {
		if (this.initialized || typeof window === "undefined") return;
		const consent = getConsentStatus();
		if (consent !== "Accepted") {
			return; // Respect user privacy
		}

		this.initialized = true;

		// Check session
		const { isNewSession } = getOrSetSessionId();
		if (isNewSession) {
			this.track("session_start", "Session Started", "session", {
				entry_page: window.location.pathname,
			});
		}

		// Initial landing page record
		if (!sessionStorage.getItem("solufy_landing_page")) {
			sessionStorage.setItem("solufy_landing_page", window.location.pathname);
		}

		// Track Initial Page View
		this.trackPageView();

		// Set up listeners
		this.setupScrollTracking();
		this.setupClickTracking();
		this.setupFormTracking();
		this.setupErrorTracking();
		this.setupNavigationTracking();
		mountEngagement(this);
		this.setupActivityKeepalive();
	}

	track(eventType, eventName, category = "general", extra = {}, immediate = false) {
		const consent = getConsentStatus();
		if (consent !== "Accepted") return;

		const vid = getOrSetVisitorId();
		const { sessionId, isNewSession } = getOrSetSessionId();
		if (isNewSession && eventType !== "session_start") {
			this.track("session_start", "Session Started", "session");
			if (!["page_view", "page_refresh"].includes(eventType)) this.track("page_view", "Page View", "page");
		}
		const client = getClientContext();
		const attribution = sessionAttribution(sessionId);
		const pageInfo = detectPageDetails();

		const landingPage = attribution.landing_page;
		const previousPage = sessionStorage.getItem("solufy_prev_page") || document.referrer || "";
		const geo = getClientGeo();
		const cookiesObj = {
			solufy_vid: vid,
			solufy_sid: sessionId,
			solufy_country: client.country || geo.country || getCookie("solufy_country") || "",
			solufy_geo: geo,
		};

		const eventObj = {
			visitor_id: vid,
			session_id: sessionId,
			event_id: generateUUID(),
			event_timestamp: new Date().toISOString(),
			event_type: eventType,
			event_name: eventName || eventType,
			event_category: category,
			page_url: safePageUrl(),
			cookie_consent: consent,
			page_title: document.title || "",
			page_type: pageInfo.pageType,
			landing_page: landingPage,
			previous_page: previousPage,
			cookies_data: JSON.stringify(cookiesObj),
			service: pageInfo.service || extra.service || "",
			service_name: pageInfo.serviceName || extra.service_name || "",
			service_url: extra.service_url || (pageInfo.service ? window.location.pathname : ""),
			industry: pageInfo.industry || extra.industry || "",
			industry_name: pageInfo.industryName || extra.industry_name || "",
			industry_url: extra.industry_url || (pageInfo.industry ? window.location.pathname : ""),
			article: pageInfo.article || extra.article || "",
			article_name: pageInfo.articleName || extra.article_name || "",
			job: pageInfo.job || extra.job || "",
			job_name: pageInfo.jobName || extra.job_name || "",
			referrer: document.referrer || "",
			referrer_domain: document.referrer ? new URL(document.referrer, window.location.href).hostname : "",
			...client,
			...attribution,
			...extra,
		};

		this.queue.push(eventObj);

		if (immediate) {
			this.flush();
		} else {
			this.scheduleFlush();
		}
	}

	trackPageView() {
		const isReload =
			(window.performance &&
				window.performance.getEntriesByType &&
				window.performance.getEntriesByType("navigation")[0]?.type === "reload") ||
			window.performance?.navigation?.type === 1;

		const eventType = isReload ? "page_refresh" : "page_view";
		const eventName = isReload ? "Page Refreshed" : "Page View";

		const pageInfo = detectPageDetails();

		// Record primary page view
		this.track(eventType, eventName, "page", {}, true);

		// Record specific content category view
		if (pageInfo.pageType === "service" && pageInfo.service) {
			this.track("service_view", `Viewed Service: ${pageInfo.serviceName || pageInfo.service}`, "services", {
				service: pageInfo.service,
				service_name: pageInfo.serviceName,
			});
		} else if (pageInfo.pageType === "industry" && pageInfo.industry) {
			this.track("industry_view", `Viewed Industry: ${pageInfo.industryName || pageInfo.industry}`, "industries", {
				industry: pageInfo.industry,
				industry_name: pageInfo.industryName,
			});
		} else if (pageInfo.pageType === "careers") {
			this.track("career_view", "Viewed Careers", "careers");
		} else if (pageInfo.pageType === "contact") {
			this.track("contact_view", "Viewed Contact Page", "contact");
		}

		// Update prev page for next navigation
		sessionStorage.setItem("solufy_prev_page", window.location.pathname);
	}

	scheduleFlush() {
		if (this.flushTimer) return;
		this.flushTimer = setTimeout(() => {
			this.flushTimer = null;
			this.flush();
		}, FLUSH_INTERVAL_MS);
	}

	flush(useBeacon = false) {
		if (getConsentStatus() !== "Accepted") { this.queue = []; return; }
		if (!this.queue.length) return;
		if (this.inFlight && !useBeacon) { this.scheduleFlush(); return; }
		const eventsToSend = [...this.queue];
		this.queue = [];

		// Backfill geolocation if resolved between queue and flush
		const geo = getClientGeo();
		if (geo && (geo.country || geo.city || geo.latitude)) {
			for (const ev of eventsToSend) {
				if (!ev.country && geo.country) ev.country = geo.country;
				if (!ev.region_state && geo.region_state) ev.region_state = geo.region_state;
				if (!ev.city && geo.city) ev.city = geo.city;
				if (!ev.latitude && geo.latitude) ev.latitude = geo.latitude;
				if (!ev.longitude && geo.longitude) ev.longitude = geo.longitude;
			}
		}

		const rawToken = window.frappe?.csrf_token;
		const csrfToken = (rawToken && rawToken !== "None" && rawToken !== "null") ? rawToken : "";

		const payloadObj = {
			events: eventsToSend,
		};
		if (csrfToken) {
			payloadObj.csrf_token = csrfToken;
		}
		const payload = JSON.stringify(payloadObj);

		if (useBeacon && navigator.sendBeacon) {
			try {
				const blob = new Blob([payload], { type: "application/json" });
				if (navigator.sendBeacon(API_BATCH_ENDPOINT, blob)) return;
			} catch (e) {}
		}

		this.inFlight = true;
		const headers = {
			"Content-Type": "application/json",
		};
		if (csrfToken) {
			headers["X-Frappe-CSRF-Token"] = csrfToken;
		}
		try {
			fetch(API_BATCH_ENDPOINT, {
				method: "POST",
				headers,
				body: payload,
				keepalive: true,
			}).then(response => {
				if (!response.ok) throw new Error("Analytics delivery failed");
				this.failedBatches = 0;
				return response.json();
			}).then(data => {
				if (data && data.country) {
					setCookie("solufy_country", data.country, 365);
					if (data.city) setCookie("solufy_city", data.city, 365);
					if (data.region_state) setCookie("solufy_region", data.region_state, 365);
					const geoObj = {
						country: data.country,
						city: data.city || "",
						region_state: data.region_state || "",
						latitude: data.latitude || 0,
						longitude: data.longitude || 0,
					};
					setCookie("solufy_geo", JSON.stringify(geoObj), 365);
					try {
						sessionStorage.setItem("solufy_geo", JSON.stringify(geoObj));
					} catch (e) {}
					clientGeoCache = geoObj;
				}
			}).catch(() => {
				if (++this.failedBatches <= 3 && getConsentStatus() === "Accepted") {
					this.queue = [...eventsToSend, ...this.queue].slice(0, 100);
					this.scheduleFlush();
				}
			}).finally(() => { this.inFlight = false; if (this.queue.length) this.scheduleFlush(); });
		} catch (e) { this.inFlight = false; }
	}

	// -----------------------------------------------------------------
	// Specialized Trackers
	// -----------------------------------------------------------------
	setupScrollTracking() {
		let ticking = false;
		window.addEventListener(
			"scroll",
			() => {
				updateLastActive();
				if (!ticking) {
					window.requestAnimationFrame(() => {
						const scrollHeight = Math.max(
							document.body.scrollHeight,
							document.documentElement.scrollHeight,
							1,
						);
						const scrolled = window.scrollY + window.innerHeight;
						const depth = Math.min(100, Math.round((scrolled / scrollHeight) * 100));
						if (depth > this.maxScrollDepth) {
							this.maxScrollDepth = depth;
						}
						ticking = false;
					});
					ticking = true;
				}
			},
			{ passive: true },
		);
	}

	setupClickTracking() {
		document.addEventListener("click", e => {
			updateLastActive();
			const target = e.target.closest("a, button, [role='button'], input[type='submit'], .btn, [data-track-click]");
			if (!target) return;

			const tag = target.tagName.toLowerCase();
			const href = target.getAttribute("href") || "";
			const text = (target.innerText || target.value || target.getAttribute("aria-label") || "").trim().slice(0, 80);
			const btnName = text || target.id || target.className || "Button";
			const isCta = target.classList.contains("btn") || target.hasAttribute("data-cta") || /get started|contact|quote|consult|demo|apply/i.test(text);

			const extra = {
				element_type: tag,
				element_id: target.id || "",
				element_class: target.className ? String(target.className).slice(0, 180) : "",
				element_text: text,
				button_name: btnName,
				destination_url: href,
				click_position: `x:${e.clientX},y:${e.clientY}`,
			};

			// Content specific detection
			const pageInfo = detectPageDetails();

			if (target.hasAttribute("data-testimonial-video")) {
				this.track("video_open", "Opened testimonial video", "media", extra);
			} else if (target.hasAttribute("data-search-chip")) {
				this.track("site_search", "Used a search topic", "search", { ...extra, element_text: target.dataset.searchChip });
			} else if (target.hasAttribute("download") || /\.(pdf|zip|docx?|xlsx?)(\?|$)/i.test(href)) {
				this.track("download", "File download", "content", extra);
			} else if (href.startsWith("tel:")) {
				this.track("service_contact_click", `Phone Click: ${text || href}`, "contact", { ...extra, cta_name: "Phone Call" }, true);
			} else if (href.startsWith("mailto:")) {
				this.track("service_contact_click", `Email Click: ${text || href}`, "contact", { ...extra, cta_name: "Email Link" }, true);
			} else if (href && !href.startsWith("#") && !href.startsWith("/") && !href.startsWith(window.location.origin)) {
				this.track("external_link_click", `External Click: ${href}`, "navigation", extra);
			} else if (/consultation|book|schedule/i.test(text)) {
				const evType = pageInfo.pageType === "industry" ? "industry_consultation_click" : "service_consultation_click";
				this.track(evType, `Consultation CTA: ${text}`, "conversion", { ...extra, cta_name: text }, true);
			} else if (pageInfo.pageType === "service" && isCta) {
				this.track("service_cta_click", `Service CTA: ${text}`, "services", { ...extra, cta_name: text });
			} else if (pageInfo.pageType === "industry" && isCta) {
				this.track("industry_cta_click", `Industry CTA: ${text}`, "industries", { ...extra, cta_name: text });
			} else if (/apply/i.test(text) || target.closest("[data-apply-job]")) {
				this.track("job_apply_click", `Job Apply Click: ${text}`, "careers", extra, true);
			} else if (/\.pdf|\.zip|\.docx?/i.test(href)) {
				this.track("download", `File Download: ${href}`, "content", extra);
			} else if (target.closest("nav") || target.closest("header") || target.closest(".solufy-nav")) {
				this.track("navigation_menu_click", `Nav Menu Click: ${text}`, "navigation", extra);
			} else if (isCta) {
				this.track("cta_click", `CTA Click: ${text}`, "interaction", { ...extra, cta_name: text });
			} else if (tag === "button" || target.getAttribute("role") === "button") {
				this.track("button_click", `Button Click: ${text}`, "interaction", extra);
			}
		});
	}

	setupFormTracking() {
		// Form view detection
		const forms = document.querySelectorAll("form, [data-contact-form], .solufy-lead-form");
		forms.forEach((form, idx) => {
			const formName = form.getAttribute("name") || form.id || `Form #${idx + 1}`;
			this.track("form_view", `Form Rendered: ${formName}`, "forms", {
				form_name: formName,
				form_id: form.id || "",
				form_action: form.getAttribute("action") || "",
				form_status: "view",
			});
		});

		// Focus / field interaction (Strictly non-sensitive: field names/types only)
		document.addEventListener("focusin", e => {
			const form = e.target.closest("form");
			if (!form) return;
			const formName = form.getAttribute("name") || form.id || "Website Form";

			if (!this.activeFormStarts.has(form)) {
				this.activeFormStarts.add(form);
				this.track("form_start", `Form Started: ${formName}`, "forms", {
					form_name: formName,
					form_id: form.id || "",
					form_status: "start",
				}, true);
			}
		});

		document.addEventListener("change", e => {
			const form = e.target.closest("form");
			if (!form || !e.target.name) return;
			const fieldName = e.target.name || e.target.id || "field";
			// Do NOT capture value - privacy first!
			this.track("form_field_interaction", `Form Field: ${fieldName}`, "forms", {
				form_name: form.getAttribute("name") || form.id || "Form",
				form_id: form.id || "",
				element_type: e.target.type || "input",
				element_id: fieldName,
				form_status: "field_interaction",
			});
		});

		// Submit
		document.addEventListener("submit", e => {
			const form = e.target;
			const formName = form.getAttribute("name") || form.id || "Form";
			this.track("form_submit", `Form Submitted: ${formName}`, "forms", {
				form_name: formName,
				form_id: form.id || "",
				form_action: form.getAttribute("action") || "",
				form_status: "submit",
			}, true);
		});
	}

	setupErrorTracking() {
		// Global JS errors
		window.addEventListener("error", e => {
			this.track("javascript_error", `JS Error: ${e.message || "Unknown error"}`, "error", {
				error_type: "javascript_error",
				error_message: `${e.message} at ${e.filename}:${e.lineno}:${e.colno}`,
			});
		});

		// Unhandled promise rejections
		window.addEventListener("unhandledrejection", e => {
			const reason = e.reason ? String(e.reason.message || e.reason) : "Unhandled rejection";
			this.track("api_error", `API/Promise Error: ${reason}`, "error", {
				error_type: "api_error",
				error_message: reason,
			});
		});

		// 404 detection
		if (
			document.title.toLowerCase().includes("404") ||
			document.title.toLowerCase().includes("not found") ||
			document.querySelector(".page-404, [data-404]")
		) {
			this.track("404", `Page Not Found: ${window.location.pathname}`, "error", {
				error_type: "404",
				error_message: `404 Not Found at ${window.location.pathname}`,
				http_status_code: "404",
			}, true);
		}
	}

	setupNavigationTracking() {
		// Detect SPA history navigation
		const originalPush = history.pushState;
		const originalReplace = history.replaceState;
		const self = this;

		history.pushState = function (...args) {
			const ret = originalPush.apply(this, args);
			self.handleHistoryChange("push");
			return ret;
		};

		history.replaceState = function (...args) {
			const ret = originalReplace.apply(this, args);
			self.handleHistoryChange("replace");
			return ret;
		};

		window.addEventListener("popstate", () => {
			this.track("browser_back", "Browser Back/Forward Nav", "navigation", {
				navigation_type: "popstate",
			});
			this.handleHistoryChange("popstate");
		});
	}

	handleHistoryChange(navType) {
		if (this.currentPath === window.location.pathname) return;
		// When route changes, track exit for old page and view for new page
        this.finishPage?.();
        this.currentPath = window.location.pathname;
        this.pageStartTime = performance.now();
        this.maxScrollDepth = 0;
		this.trackPageView();
	}


	setupActivityKeepalive() {
		["mousedown", "keydown", "touchstart", "scroll"].forEach(evt => {
			window.addEventListener(evt, updateLastActive, { passive: true });
		});
	}
}

// Global Singleton
export const tracker = new VisitorTracker();

document.addEventListener("solufy:consent-changed", () => {
 if (!tracker.initialized) tracker.init();
});

export function mountTracker() {
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => tracker.init(), { once: true });
	} else {
		tracker.init();
	}
}

